import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { readApiError, roverNotificationsApi } from '@/api/Rover/notifications/roverNotifications';
import {
  CHANNEL_LABELS,
  CHANNELS,
  type Channel,
  type ChannelContent,
  type ChannelEnabled,
  type MessageOption,
  type MessageSource,
  type StatusNotificationConfig,
  type StatusNotificationSummary,
} from '@/api/Rover/notifications/roverNotifications.types';
import { applicationFormNotificationsClient } from '@/features/ApplicationForm/utils/notifications/applicationFormNotificationsClient';
import { hasCustomContent, resolveSource, smsSegments } from '@/features/ApplicationForm/utils/notifications/renderMessage';
import {
  type ApplicationFormStatusCatalogItem,
  buildStaticBootstrap,
  isStaticStatusKey,
  staticStatusLabelFromKey,
} from '@/features/ApplicationForm/utils/notifications/staticDefaults';
import { resolveStatusLabelFromApplicationCode } from '@/features/ApplicationForm/utils/notifications/statusCatalog';
import { sanitizeResponseError } from '@/utils';
import { ChannelPreviewModal } from './components/ChannelPreviewModal';
import { MergeFieldBar } from './components/MergeFieldBar';
import { MessageOptionsManager } from './components/MessageOptionsManager';
import { Button, ConfirmDialog, cx, Notice, SourceBadge, Switch } from './ui/primitives';

export type FormNotificationsPageMode = 'rover' | 'applicationForm';

export interface FormNotificationsPageProps {
  formId: string;
  /** Used in previews for {{program_name}}. */
  programName: string;
  /** True when the user may edit Form Settings (wrap the route in RequirePrivilege too). */
  canEdit: boolean;
  initialStatus?: string;
  /** `applicationForm` uses GET/POST /application_forms/:id/notifications and shows static UI before the API responds. */
  mode?: FormNotificationsPageMode;
  /** Application.status code (e.g. DOCUMENT_REVIEW) to select after staff changes status on application details. */
  highlightApplicationStatusCode?: string | null;
}

type FieldKey = 'email.subject' | 'email.body' | 'sms.body' | 'onscreen.title' | 'onscreen.body';
type Draft = { content: ChannelContent; enabled: ChannelEnabled };

const getField = (c: ChannelContent, key: FieldKey): string | null => {
  const [ch, f] = key.split('.') as [Channel, string];
  return (c[ch] as Record<string, string | null>)[f];
};
const setField = (c: ChannelContent, key: FieldKey, value: string | null): ChannelContent => {
  const [ch, f] = key.split('.') as [Channel, string];
  return { ...c, [ch]: { ...c[ch], [f]: value } };
};

const METHOD_DESCRIPTIONS: Record<Channel, string> = {
  email: 'To the applicant’s email',
  sms: 'To the applicant’s phone',
  onscreen: 'On the applicant status page',
};

const summarySource = (content: ChannelContent, activeOptionCount: number): MessageSource =>
  activeOptionCount > 0 ? 'options' : CHANNELS.some((ch) => hasCustomContent(ch, content)) ? 'custom' : 'default';

/** One row per label — avoids duplicate sidebar entries if the API returns repeats. */
const dedupeStatusSummaries = (list: StatusNotificationSummary[]) => {
  const seen = new Set<string>();
  return list.filter((s) => {
    if (seen.has(s.statusLabel)) return false;
    seen.add(s.statusLabel);
    return true;
  });
};

const resolveServerStatusId = (
  config: StatusNotificationConfig,
  selected: string | null,
  serverSummaries: StatusNotificationSummary[] | null
): string | null => {
  for (const id of [selected, config.status]) {
    if (id && !isStaticStatusKey(id)) return id;
  }
  return serverSummaries?.find((s) => s.statusLabel === config.statusLabel && !isStaticStatusKey(s.status))?.status ?? null;
};

export function FormNotificationsPage({
  formId,
  programName,
  canEdit,
  initialStatus,
  mode = 'rover',
  highlightApplicationStatusCode = null,
}: FormNotificationsPageProps) {
  const [statusCatalog, setStatusCatalog] = useState<ApplicationFormStatusCatalogItem[]>([]);
  const staticBootstrap = useMemo(
    () => (mode === 'applicationForm' && statusCatalog.length > 0 ? buildStaticBootstrap(statusCatalog) : null),
    [mode, statusCatalog]
  );
  const staticSummaries = useMemo(() => staticBootstrap?.summaries ?? [], [staticBootstrap]);

  /** Rover API status list (applicationForm mode uses `serverSummaries` + catalog fallback). */
  const [statuses, setStatuses] = useState<StatusNotificationSummary[] | null>(null);
  const [serverSummaries, setServerSummaries] = useState<StatusNotificationSummary[] | null>(null);
  const [notificationsSchemaReady, setNotificationsSchemaReady] = useState<boolean | null>(null);
  const [notificationsSchemaError, setNotificationsSchemaError] = useState<string | null>(null);
  const sidebarStatuses =
    mode === 'applicationForm' ? (serverSummaries && serverSummaries.length > 0 ? serverSummaries : staticSummaries) : statuses;

  const [selected, setSelected] = useState<string | null>(initialStatus ?? null);
  const [config, setConfig] = useState<StatusNotificationConfig | null>(null);
  const [options, setOptions] = useState<MessageOption[]>([]);
  const [savedOptionsBaseline, setSavedOptionsBaseline] = useState<MessageOption[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [view, setView] = useState<'message' | 'options'>('message');
  const [channel, setChannel] = useState<Channel>('email');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);

  const fieldRefs = useRef<Partial<Record<FieldKey, HTMLInputElement | HTMLTextAreaElement | null>>>({});
  const lastField = useRef<FieldKey>('email.body');

  const applyStatusDetail = useCallback((c: StatusNotificationConfig, opts: MessageOption[]) => {
    setConfig(c);
    setOptions(opts);
    setSavedOptionsBaseline(structuredClone(opts));
    setDraft({ content: structuredClone(c.content), enabled: { ...c.enabled } });
    setSavedAt(null);
  }, []);

  // Status list
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        if (mode === 'applicationForm') {
          const {
            summaries,
            schemaReady,
            statusCatalog: catalog,
            schemaError,
          } = await applicationFormNotificationsClient.listStatuses(formId);
          if (cancelled) return;
          setStatusCatalog(catalog);
          setNotificationsSchemaReady(schemaReady);
          setNotificationsSchemaError(schemaError ?? null);
          const normalized = dedupeStatusSummaries(summaries);
          const catalogBootstrap = catalog.length > 0 ? buildStaticBootstrap(catalog) : null;
          if (schemaReady && normalized.length > 0) {
            setServerSummaries(normalized);
            setSelected((prev) => {
              const base = prev ?? catalogBootstrap?.first ?? normalized[0]?.status ?? null;
              if (!base) return null;
              if (isStaticStatusKey(base)) {
                const label = staticStatusLabelFromKey(base, catalog);
                return normalized.find((s) => s.statusLabel === label)?.status ?? normalized[0]?.status ?? base;
              }
              return normalized.find((s) => s.status === base)?.status ?? normalized[0]?.status ?? base;
            });
          } else {
            setServerSummaries(null);
            setSelected((prev) => prev ?? catalogBootstrap?.first ?? null);
          }
        } else {
          const list = await roverNotificationsApi.listStatuses(formId);
          if (cancelled) return;
          const normalized = dedupeStatusSummaries(list);
          setStatuses(normalized);
          setSelected((prev) => {
            if (!prev) return normalized[0]?.status ?? null;
            return normalized.find((s) => s.status === prev)?.status ?? normalized[0]?.status ?? prev;
          });
        }
      } catch (e) {
        if (cancelled) return;
        if (mode !== 'applicationForm') {
          setError(readApiError(e).message);
        }
        // applicationForm: keep static sidebar; no error banner (API sync is best-effort).
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [formId, mode]);

  useEffect(() => {
    if (!highlightApplicationStatusCode || !sidebarStatuses?.length) return;
    const label = resolveStatusLabelFromApplicationCode(highlightApplicationStatusCode, statusCatalog);
    const match = sidebarStatuses.find((s) => s.status === highlightApplicationStatusCode || (label != null && s.statusLabel === label));
    if (match) setSelected(match.status);
  }, [highlightApplicationStatusCode, statusCatalog, sidebarStatuses]);

  // Selected status
  useEffect(() => {
    if (!selected) return;
    if (mode === 'applicationForm' && isStaticStatusKey(selected)) {
      const detail = staticBootstrap?.load(selected);
      if (detail) applyStatusDetail(detail.config, detail.options);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    const loadDetail = async () => {
      try {
        if (mode === 'applicationForm') {
          const { config: c, options: opts } = await applicationFormNotificationsClient.loadStatusDetail(formId, selected);
          if (cancelled) return;
          applyStatusDetail(c, opts);
        } else {
          const [c, opts] = await Promise.all([
            roverNotificationsApi.getStatus(formId, selected),
            roverNotificationsApi.listOptions(formId, selected),
          ]);
          if (cancelled) return;
          applyStatusDetail(c, opts);
        }
      } catch (e) {
        if (!cancelled) setError(readApiError(e).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void loadDetail();
    return () => {
      cancelled = true;
    };
  }, [formId, selected, mode, staticBootstrap, applyStatusDetail]);

  const highlightedStatusLabel = useMemo(
    () => resolveStatusLabelFromApplicationCode(highlightApplicationStatusCode, statusCatalog),
    [highlightApplicationStatusCode, statusCatalog]
  );

  const dirty = useMemo(
    () =>
      !!config &&
      !!draft &&
      (JSON.stringify(draft.content) !== JSON.stringify(config.content) ||
        JSON.stringify(draft.enabled) !== JSON.stringify(config.enabled) ||
        JSON.stringify(options) !== JSON.stringify(savedOptionsBaseline)),
    [config, draft, options, savedOptionsBaseline]
  );

  // Warn before leaving with unsaved edits.
  // TODO(integration): if the app uses a data router, also add useBlocker for in-app navigation.
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const activeOptionCount = options.filter((o) => o.isActive).length;

  const syncSummary = useCallback((status: string, patch: Partial<StatusNotificationSummary>) => {
    const mapList = (list: StatusNotificationSummary[] | null) => list?.map((s) => (s.status === status ? { ...s, ...patch } : s)) ?? list;
    setStatuses(mapList);
    setServerSummaries(mapList);
  }, []);

  const selectStatus = (status: string) => {
    if (status === selected) return;
    if (dirty) setPendingStatus(status);
    else {
      setSelected(status);
      setView('message');
    }
  };

  const save = async () => {
    if (!config || !draft) return;
    setSaving(true);
    setError(null);
    try {
      if (mode === 'applicationForm') {
        let schemaReadyForSave = notificationsSchemaReady;
        let schemaErrorForSave = notificationsSchemaError;
        let statusId = resolveServerStatusId(config, selected, serverSummaries);
        if (!statusId) {
          const fresh = await applicationFormNotificationsClient.listStatuses(formId);
          schemaReadyForSave = fresh.schemaReady;
          schemaErrorForSave = fresh.schemaError ?? null;
          setNotificationsSchemaReady(fresh.schemaReady);
          setNotificationsSchemaError(schemaErrorForSave);
          if (fresh.schemaReady && fresh.summaries.length > 0) {
            setServerSummaries(fresh.summaries);
            statusId = resolveServerStatusId(config, selected, fresh.summaries);
          }
        }
        if (!statusId) {
          if (schemaReadyForSave === false) {
            setError(
              schemaErrorForSave
                ? `Notification storage could not be initialized: ${schemaErrorForSave}`
                : 'Notification storage could not be initialized. Restart the API server and reload this page. If it persists, check backend logs for [notifications].'
            );
          } else {
            setError('Could not resolve this status on the server. Wait a moment for statuses to load, then try again.');
          }
          setSaving(false);
          return;
        }
        const saved = await applicationFormNotificationsClient.saveStatus(formId, statusId, draft, options);
        applyStatusDetail(saved.config, saved.options);
        syncSummary(saved.config.status, {
          source: saved.config.source,
          enabled: saved.config.enabled,
          activeOptionCount: saved.config.activeOptionCount,
        });
      } else {
        const updated = await roverNotificationsApi.updateStatus(formId, config.status, {
          enabled: draft.enabled,
          content: draft.content,
        });
        setConfig(updated);
        setDraft({ content: structuredClone(updated.content), enabled: { ...updated.enabled } });
        syncSummary(updated.status, { source: updated.source, enabled: updated.enabled });
      }
      setSavedAt(Date.now());
    } catch (e) {
      setError(mode === 'applicationForm' ? sanitizeResponseError(e) : readApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!config) return;
    setConfirmReset(false);
    setSaving(true);
    try {
      if (mode === 'applicationForm') {
        const statusId = resolveServerStatusId(config, selected, serverSummaries);
        if (!statusId) {
          if (notificationsSchemaReady === false) {
            setError(
              notificationsSchemaError
                ? `Notification storage could not be initialized: ${notificationsSchemaError}`
                : 'Notification storage could not be initialized. Restart the API server and reload this page.'
            );
          } else {
            setError('Could not resolve this status on the server. Wait a moment for statuses to load, then try again.');
          }
          setSaving(false);
          return;
        }
        const saved = await applicationFormNotificationsClient.resetStatus(formId, statusId);
        applyStatusDetail(saved.config, saved.options);
        syncSummary(saved.config.status, { source: saved.config.source, activeOptionCount: 0 });
      } else {
        const updated = await roverNotificationsApi.resetStatus(formId, config.status);
        setConfig(updated);
        setDraft({ content: structuredClone(updated.content), enabled: { ...updated.enabled } });
        syncSummary(updated.status, { source: updated.source });
      }
    } catch (e) {
      setError(mode === 'applicationForm' ? sanitizeResponseError(e) : readApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const onOptionsChange = (next: MessageOption[]) => {
    setOptions(next);
    if (mode !== 'applicationForm') setSavedOptionsBaseline(structuredClone(next));
    if (!config) return;
    const count = next.filter((o) => o.isActive).length;
    setConfig({ ...config, activeOptionCount: count, source: summarySource(config.content, count) });
    syncSummary(config.status, { activeOptionCount: count, source: summarySource(config.content, count) });
  };

  const onSettingsChange = async (patch: Parameters<typeof roverNotificationsApi.updateStatus>[2]) => {
    if (!config) return;
    setConfig({ ...config, ...patch } as StatusNotificationConfig);
    if (mode === 'applicationForm') return;
    try {
      const updated = await roverNotificationsApi.updateStatus(formId, config.status, patch);
      setConfig((c) => (c ? { ...c, optionsSelectionMode: updated.optionsSelectionMode, optionsFormat: updated.optionsFormat } : c));
    } catch (e) {
      setConfig(config);
      setError(readApiError(e).message);
    }
  };

  const updateField = (key: FieldKey, value: string) =>
    setDraft((d) => (d ? { ...d, content: setField(d.content, key, value === '' ? null : value) } : d));

  const insertToken = (token: string) => {
    if (!draft) return;
    const key = lastField.current.startsWith(channel) ? lastField.current : (`${channel}.body` as FieldKey);
    const el = fieldRefs.current[key];
    const current = getField(draft.content, key) ?? '';
    const start = el?.selectionStart ?? current.length;
    const end = el?.selectionEnd ?? start;
    updateField(key, current.slice(0, start) + token + current.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const startFromDefault = () => {
    if (!config || !draft) return;
    const d = config.defaults[channel];
    setDraft({ ...draft, content: { ...draft.content, [channel]: { ...d } } });
  };

  const setEnabled = (ch: Channel, on: boolean) => setDraft((d) => (d ? { ...d, enabled: { ...d.enabled, [ch]: on } } : d));

  const renderField = (key: FieldKey, label: string, multiline: boolean, rows = 8) => {
    if (!draft || !config) return null;
    const value = getField(draft.content, key) ?? '';
    const placeholder = getField(config.defaults, key) ?? '';
    const common = {
      value,
      placeholder,
      disabled: !canEdit,
      onFocus: () => (lastField.current = key),
      className:
        'rounded-md border border-gray-300 px-3 text-[15px] font-normal text-gray-900 placeholder:text-gray-400 disabled:bg-gray-50',
    };
    return (
      <label className='flex flex-col gap-1.5 text-sm font-semibold text-gray-700'>
        {label}
        {multiline ? (
          <textarea
            {...common}
            ref={(el) => {
              fieldRefs.current[key] = el;
            }}
            rows={rows}
            onChange={(e) => updateField(key, e.target.value)}
            className={cx(common.className, 'resize-y py-3 leading-relaxed')}
          />
        ) : (
          <input
            {...common}
            ref={(el) => {
              fieldRefs.current[key] = el;
            }}
            type='text'
            onChange={(e) => updateField(key, e.target.value)}
            className={cx(common.className, 'h-11')}
          />
        )}
      </label>
    );
  };

  const onCount = draft ? CHANNELS.filter((c) => draft.enabled[c]).length : 0;
  const channelSource = draft ? resolveSource(channel, draft.content, activeOptionCount) : 'default';
  const usingDefault = draft ? !hasCustomContent(channel, draft.content) : false;
  const smsInfo = draft ? smsSegments(draft.content.sms.body ?? config?.defaults.sms.body ?? '') : null;

  return (
    <section className='flex min-h-[720px] flex-col rounded-xl border border-gray-200 shadow-sm'>
      <header className='flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 px-7 py-5'>
        <div className='flex flex-col gap-1'>
          <h1 className='text-xl font-semibold text-gray-900'>Notifications</h1>
          <p className='text-sm text-gray-600'>Set the message applicants receive when their application moves to each status.</p>
        </div>
        <div className='flex flex-wrap gap-4 text-sm text-gray-600'>
          <span className='flex items-center gap-1.5'>
            <SourceBadge source='default' /> system message
          </span>
          <span className='flex items-center gap-1.5'>
            <SourceBadge source='custom' /> your message
          </span>
          <span className='flex items-center gap-1.5'>
            <SourceBadge source='options' /> staff pick in a modal
          </span>
        </div>
      </header>

      <div className='flex min-h-0 flex-1 flex-col md:flex-row'>
        <nav aria-label='Statuses' className='w-full shrink-0 border-b border-gray-200 p-3 md:w-80 md:border-b-0 md:border-r'>
          <div className='px-3 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-gray-600'>Status</div>
          {(mode === 'rover' && !sidebarStatuses) || (mode === 'applicationForm' && statusCatalog.length === 0) ? (
            <p className='px-3 py-2 text-sm text-gray-600'>Loading…</p>
          ) : null}
          <ul className='flex flex-col gap-0.5'>
            {sidebarStatuses?.map((s) => (
              <li key={s.status}>
                <button
                  type='button'
                  aria-current={s.status === selected ? 'true' : undefined}
                  onClick={() => selectStatus(s.status)}
                  className={cx(
                    'flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-3 text-left text-sm',
                    'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600',
                    s.status === selected ? 'bg-emerald-50 font-semibold text-emerald-900' : 'font-medium text-gray-800 hover:bg-gray-50',
                    highlightedStatusLabel && s.statusLabel === highlightedStatusLabel && s.status !== selected
                      ? 'ring-2 ring-amber-400 ring-offset-1'
                      : null
                  )}
                >
                  <span>{s.statusLabel}</span>
                  <span className='flex items-center gap-1.5'>
                    {!CHANNELS.some((c) => s.enabled[c]) && <span className='text-xs text-gray-500'>Off</span>}
                    <SourceBadge source={s.source} />
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <div className='flex min-w-0 flex-1 flex-col gap-5 px-8 py-6'>
          {error && <Notice tone='danger'>{error}</Notice>}
          {mode === 'rover' && (loading || !config || !draft) && !error && <p className='text-sm text-gray-600'>Loading…</p>}
          {mode === 'applicationForm' && loading && config && draft && <p className='text-sm text-gray-600'>Refreshing saved settings…</p>}

          {config && draft && (mode === 'applicationForm' || !loading) && (
            <>
              <div className='flex items-center justify-between gap-4'>
                <div className='flex items-center gap-3'>
                  <h2 className='text-2xl font-semibold text-gray-900'>{config.statusLabel}</h2>
                  <SourceBadge source={summarySource(draft.content, activeOptionCount)} />
                </div>
                {config.updatedAt && (
                  <span className='text-sm text-gray-600'>
                    Last edited {new Date(config.updatedAt).toLocaleDateString()}
                    {config.updatedBy ? ` by ${config.updatedBy.name}` : ''}
                  </span>
                )}
              </div>

              <div role='tablist' aria-label='Status settings' className='inline-flex gap-1 self-start rounded-lg bg-gray-100 p-1'>
                {(['message', 'options'] as const).map((v) => (
                  <button
                    key={v}
                    role='tab'
                    type='button'
                    aria-selected={view === v}
                    onClick={() => setView(v)}
                    className={cx(
                      'flex min-h-10 items-center gap-2 rounded-md px-4 text-sm font-semibold',
                      view === v ? 'bg-white text-emerald-800 shadow-sm' : 'text-gray-700'
                    )}
                  >
                    {v === 'message' ? 'Custom message' : 'Message options'}
                    {v === 'options' && <span className='rounded-full bg-orange-50 px-2 text-xs text-orange-800'>{activeOptionCount}</span>}
                  </button>
                ))}
              </div>

              {view === 'options' ? (
                <MessageOptionsManager
                  formId={formId}
                  config={config}
                  options={options}
                  onOptionsChange={onOptionsChange}
                  onSettingsChange={onSettingsChange}
                  canEdit={canEdit}
                  optionsPersistence={mode === 'applicationForm' ? 'local' : 'api'}
                />
              ) : (
                <>
                  <fieldset className='flex flex-col gap-3 rounded-lg border border-gray-200 px-5 py-4'>
                    <legend className='sr-only'>Notification methods for this status</legend>
                    <div className='flex justify-between'>
                      <span className='text-sm font-semibold text-gray-900'>Notification methods for this status</span>
                      <span className='text-sm text-gray-600'>{onCount} of 3 on</span>
                    </div>
                    <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
                      {CHANNELS.map((ch) => (
                        <div
                          key={ch}
                          className={cx(
                            'flex items-center justify-between gap-3 rounded-lg border px-4 py-3',
                            draft.enabled[ch] ? 'border-emerald-200 bg-emerald-50/60' : 'border-gray-300 bg-gray-50'
                          )}
                        >
                          <div className='flex min-w-0 flex-col'>
                            <span className='text-sm font-semibold'>{CHANNEL_LABELS[ch]}</span>
                            <span className='text-xs text-gray-600'>{METHOD_DESCRIPTIONS[ch]}</span>
                          </div>
                          <Switch
                            checked={draft.enabled[ch]}
                            disabled={!canEdit}
                            onChange={(on) => setEnabled(ch, on)}
                            label={`Send ${CHANNEL_LABELS[ch]} for ${config.statusLabel}`}
                          />
                        </div>
                      ))}
                    </div>
                    {onCount === 0 && (
                      <Notice tone='danger'>
                        All methods are off. Applicants won't be notified when an application moves to {config.statusLabel}.
                      </Notice>
                    )}
                  </fieldset>

                  <div className='flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-5 py-3 text-sm'>
                    <span className='font-semibold text-gray-700'>What gets sent ({CHANNEL_LABELS[channel]})</span>
                    <SourceStep
                      n={1}
                      label='Message options'
                      active={channelSource === 'options'}
                      detail={activeOptionCount > 0 ? `${activeOptionCount} active · staff pick in modal` : 'None set up'}
                    />
                    <span aria-hidden='true' className='text-gray-400'>
                      ›
                    </span>
                    <SourceStep
                      n={2}
                      label='Custom message'
                      active={!usingDefault}
                      detail={usingDefault ? 'Not set' : channelSource === 'options' ? 'Set · options merged in' : 'Set · sent as is'}
                    />
                    <span aria-hidden='true' className='text-gray-400'>
                      ›
                    </span>
                    <SourceStep
                      n={3}
                      label='Default message'
                      active={usingDefault}
                      detail={usingDefault ? 'In use' : 'Fallback · not used'}
                    />
                  </div>

                  <div role='tablist' aria-label='Channel' className='flex gap-1 border-b border-gray-300'>
                    {CHANNELS.map((ch) => (
                      <button
                        key={ch}
                        role='tab'
                        type='button'
                        aria-selected={channel === ch}
                        onClick={() => setChannel(ch)}
                        className={cx(
                          '-mb-px flex min-h-11 items-center gap-2 border-b-[3px] px-4 text-[15px] font-semibold',
                          channel === ch ? 'border-emerald-700 text-emerald-800' : 'border-transparent text-gray-600'
                        )}
                      >
                        {CHANNEL_LABELS[ch]}
                        <span
                          className={cx(
                            'rounded-full px-2 py-0.5 text-xs',
                            draft.enabled[ch] ? 'bg-emerald-100 text-emerald-900' : 'bg-gray-100 text-gray-600'
                          )}
                        >
                          {draft.enabled[ch] ? 'On' : 'Off'}
                        </span>
                      </button>
                    ))}
                  </div>

                  {!draft.enabled[channel] && (
                    <div className='flex items-center justify-between gap-3 rounded-md bg-gray-100 px-3 py-2.5 text-sm text-gray-800'>
                      <span>{CHANNEL_LABELS[channel]} is off for this status. Your message is kept but won't be sent.</span>
                      {canEdit && (
                        <Button variant='outline' className='!min-h-9' onClick={() => setEnabled(channel, true)}>
                          Turn on
                        </Button>
                      )}
                    </div>
                  )}

                  {usingDefault && canEdit && (
                    <div className='flex items-center justify-between gap-3 rounded-md bg-blue-50 px-3 py-2.5 text-sm text-blue-900'>
                      <span>This channel uses the default message (shown in grey). Type to write your own.</span>
                      <Button className='!min-h-9' onClick={startFromDefault}>
                        Start from default
                      </Button>
                    </div>
                  )}

                  <div className='flex flex-col gap-4'>
                    {channel === 'email' && (
                      <>
                        {renderField('email.subject', 'Email subject', false)}
                        {renderField('email.body', 'Email message', true, 11)}
                      </>
                    )}
                    {channel === 'sms' && (
                      <>
                        {renderField('sms.body', 'SMS message', true, 4)}
                        <div className='flex justify-between gap-4 text-sm text-gray-600'>
                          <span>
                            Options are sent by their label, separated by commas. Merge fields expand when sent, so the real length will
                            differ.
                          </span>
                          {smsInfo && (
                            <span className='shrink-0 font-semibold'>
                              {smsInfo.characters} characters · {smsInfo.segments} {smsInfo.segments === 1 ? 'text' : 'texts'}
                            </span>
                          )}
                        </div>
                      </>
                    )}
                    {channel === 'onscreen' && (
                      <>
                        {renderField('onscreen.title', 'On-screen title', false)}
                        {renderField('onscreen.body', 'On-screen message', true, 6)}
                        <span className='text-sm text-gray-600'>
                          Shown on the applicant's status page. For Application Received, this is the confirmation message shown after
                          submitting.
                        </span>
                      </>
                    )}
                  </div>

                  {canEdit && <MergeFieldBar onInsert={insertToken} />}
                  <Notice tone='warning'>
                    {'{{selected_options}}'} is replaced with the options staff pick when they change an application to this status. If it's
                    missing, the picks are added to the end of the message.
                  </Notice>

                  <div className='mt-auto flex flex-wrap items-center justify-between gap-3 pt-2'>
                    {canEdit ? (
                      <Button variant='ghost' className='underline' onClick={() => setConfirmReset(true)} disabled={saving}>
                        Reset to default message
                      </Button>
                    ) : (
                      <span />
                    )}
                    <div className='flex items-center gap-3'>
                      <span className='text-sm text-gray-600' aria-live='polite'>
                        {dirty ? 'Unsaved changes' : savedAt ? 'Saved' : ''}
                      </span>
                      <Button variant='outline' onClick={() => setPreviewOpen(true)}>
                        {channel === 'email' ? 'Preview email' : channel === 'sms' ? 'Preview SMS' : 'Preview on-screen message'}
                      </Button>
                      {canEdit && (
                        <Button variant='primary' onClick={save} disabled={!dirty || saving}>
                          {saving ? 'Saving…' : 'Save changes'}
                        </Button>
                      )}
                    </div>
                  </div>
                </>
              )}

              <ChannelPreviewModal
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                formId={formId}
                programName={programName}
                status={config.status}
                statusLabel={config.statusLabel}
                channel={channel}
                content={draft.content}
                defaults={config.defaults}
                options={options}
                optionsFormat={config.optionsFormat}
                enableTestSend={mode !== 'applicationForm'}
              />
            </>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!pendingStatus}
        title='Discard unsaved changes?'
        message="You have changes to this status that haven't been saved."
        confirmLabel='Discard changes'
        tone='danger'
        onCancel={() => setPendingStatus(null)}
        onConfirm={() => {
          setSelected(pendingStatus);
          setView('message');
          setPendingStatus(null);
        }}
      />
      <ConfirmDialog
        open={confirmReset}
        title='Reset to the default message?'
        message='Your custom email, SMS and on-screen text for this status will be removed. Message options and on/off settings are kept.'
        confirmLabel='Reset'
        tone='danger'
        onCancel={() => setConfirmReset(false)}
        onConfirm={() => void reset()}
      />
    </section>
  );
}

function SourceStep({ n, label, detail, active }: { n: number; label: string; detail: string; active: boolean }) {
  return (
    <div
      className={cx(
        'flex min-w-40 flex-1 flex-col rounded-md border px-3 py-2',
        active ? 'border-emerald-300 bg-white' : 'border-dashed border-gray-300 bg-gray-100'
      )}
    >
      <span className={cx('text-sm font-semibold', active ? 'text-emerald-800' : 'text-gray-600')}>
        {n} · {label}
      </span>
      <span className='text-xs text-gray-600'>{detail}</span>
    </div>
  );
}
